// @flow

import * as React from 'react'
import moment from 'moment'
// $FlowFixMe
import 'moment/locale/it'
import { Loader, Dimmer } from 'semantic-ui-react'
import { connect, type Connector } from 'react-redux'

import {
  toggleFilter,
  openDailyDetails,
  closeDailyDetails,
  getDailyDetailedData,
  setChartSampling,
  getDailyData,
  getWeeklyData,
  getMonthlyData,
} from '../store/reports'
import { isViewer } from '../store/authentication'
import MonthlyModal from './monthly-modal'
import CalendarCard from './calendar-card'
import './calendar-report.css'

import DownloadXLSX from '../systemwide/download-xlsx'
import ChartFilterToolbar from './chart-filter-toolbar'
import ChartSamplingToolbar from './chart-sampling-toolbar'
import ReportPager from './calendar-report-pager'

import type { ReduxState, Dispatch } from '../store'
import type { DailyDetailsObjectList, ChartSampling } from '../store/reports'

export type RangedAggregatedVisits = {
  date: string,
  name: string,
  web: number,
  f2f: number,
  phone: number,
}

type OwnProps = {||}

type MappedProps = {|
  currentCampaign: string,
  status: string,
  modalLoading: string,
  chartSampling: ChartSampling,
  showWeb: boolean,
  showPhone: boolean,
  showF2F: boolean,
  data: Array<RangedAggregatedVisits>,
  selectedDay: string,
  selectedDayDetails: DailyDetailsObjectList,
  reportStartDate: string,
  isViewer: boolean,
|}

const mapStateToProps = (state: ReduxState, ownProps: OwnProps): MappedProps => {
  let data = []
  const reportStartDate = state.reports.ui.reportStartDate

  if (Object.keys(state.reports.ui.data).length > 0 && moment(state.reports.ui.reportStartDate).isValid()) {
    if (state.reports.ui.chartSampling === 'DAILY') {
      let startMonth = moment(reportStartDate).startOf('month')
      let endMonth = moment(reportStartDate).endOf('month')
      data = getDailyData(state, moment(startMonth).startOf('isoweek'), moment(endMonth).endOf('isoweek')).map(d => {
        return { ...d, disabled: !moment(d.date, 'YYYYMMDD').isBetween(startMonth, endMonth, null, '[]') }
      })
    } else if (state.reports.ui.chartSampling === 'WEEKLY') {
      data = getWeeklyData(state, moment(reportStartDate).startOf('quarter'), moment(reportStartDate).endOf('quarter'))
    } else if (state.reports.ui.chartSampling === 'MONTHLY') {
      data = getMonthlyData(state, moment(reportStartDate).startOf('year'), moment(reportStartDate).endOf('year'))
    }
  }

  return {
    currentCampaign: state.allowedCampaigns.currentCampaign,
    status: state.reports.ui.loading,
    modalLoading: state.reports.ui.modalLoading,
    chartSampling: state.reports.ui.chartSampling,
    showWeb: state.reports.ui.showWeb,
    showPhone: state.reports.ui.showPhone,
    showF2F: state.reports.ui.showF2F,
    data,
    reportStartDate,
    selectedDay: state.reports.ui.selectedDay,
    selectedDayDetails: state.reports.ui.selectedDayDetails
      ? state.reports.ui.selectedDayDetails
      : { WEB: [], PHONE: [], F2F: [] },
    isViewer: isViewer(state),
  }
}

type HandlerValueArg = { value: string }
type Props = { dispatch: Dispatch } & OwnProps & MappedProps
class CalendarReport extends React.Component<Props> {
  static defaultProps = {}

  updateCalendar = () => {
    let cards = []
    let totals = { web: 0, phone: 0, f2f: 0 }
    for (let d of this.props.data) {
      totals.web += d.web
      totals.phone += d.phone
      totals.f2f += d.f2f
      cards.push(
        <CalendarCard
          showWeb={this.props.showWeb}
          showPhone={this.props.showPhone}
          showF2F={this.props.showF2F}
          disabled={d.disabled ? true : false}
          chartSampling={this.props.chartSampling}
          date={d.date}
          data={d}
          key={`day-${d.date}-${this.props.chartSampling}`}
          showDetails={this.showDetails}
          isViewer={this.props.isViewer}
        />
      )
    }
    // FIX LAYOUT ON FHD+ SCREEN
    if (cards.length > 0) {
      if (cards.length < 5) cards.push(<div key={`key-padder-${Math.random()}`} className="padder" />)
      if (cards.length < 10) cards.push(<div key={`key-padder-${Math.random()}`} className="padder" />)
      if (cards.length < 16) cards.push(<div key={`key-padder-${Math.random()}`} className="padder" />)
    }
    return { cards, totals }
  }

  onToggleFilter = (filter: 'web' | 'phone' | 'f2f') => {
    this.props.dispatch(toggleFilter(filter))
  }

  handleClose = () => {
    this.props.dispatch(closeDailyDetails())
  }

  showDetails = (e: SyntheticEvent<any>, { value }: HandlerValueArg) => {
    let startDate = moment(value, 'YYYYMMDD')
    let endDate = moment(value, 'YYYYMMDD')
    if (this.props.chartSampling === 'WEEKLY') {
      endDate = moment(value, 'YYYYMMDD').endOf('isoweek')
    } else if (this.props.chartSampling === 'MONTHLY') {
      endDate = moment(value, 'YYYYMMDD').endOf('month')
    }
    this.props.dispatch(openDailyDetails(value))
    this.props.dispatch(
      getDailyDetailedData(this.props.currentCampaign, startDate.format('YYYYMMDD'), endDate.format('YYYYMMDD'))
    )
  }

  onChangeSampling = (chartSampling: ChartSampling) => {
    this.props.dispatch(setChartSampling(chartSampling))
  }

  render() {
    let { cards, totals } = this.updateCalendar()
    let totWeb = totals.web
    let totPhone = totals.phone
    let totF2F = totals.f2f

    return (
      <Dimmer.Dimmable
        dimmed={this.props.status === 'NEW' || this.props.status === 'PENDING'}
        className="main-container">
        <Dimmer
          active={this.props.status === 'NEW' || this.props.status === 'PENDING'}
          inverted
          style={{ zIndex: 700 }}>
          {this.props.status === 'PENDING' && <Loader />}
        </Dimmer>
        <div className="ui column grid">
          <MonthlyModal
            selectedDay={this.props.selectedDay}
            modalLoading={this.props.modalLoading}
            selectedDayDetails={this.props.selectedDayDetails}
            handleClose={this.handleClose}
            showWeb={this.props.showWeb}
            showPhone={this.props.showPhone}
            showF2F={this.props.showF2F}
            sampling={this.props.chartSampling}
          />
          <div className="column">
            <div style={{ float: 'left' }}>
              <ReportPager />
              <span style={{ display: 'inline-block', width: '2ex' }} />
              <ChartSamplingToolbar chartSampling={this.props.chartSampling} onChangeSampling={this.onChangeSampling} />
              <span style={{ display: 'inline-block', width: '2ex' }} />
              {this.props.isViewer === false && (
                <DownloadXLSX
                  size="small"
                  style={{ minWidth: '20ex' }}
                  icon="download"
                  color="orange"
                  labelPosition="left"
                  label="Esporta"
                  filename="report_visite_giorno.xlsx"
                  sheetname="export"
                  data={this.props.data
                    .filter(r => typeof r.disabled === 'undefined' || r.disabled !== true)
                    .map(r => ({
                      start_date: parseInt(r.date, 10),
                      data_range: r.name,
                      web: r.web,
                      phone: r.phone,
                      f2f: r.f2f,
                    }))}
                  mode="button"
                />
              )}
            </div>
            <div style={{ float: 'right', textAlign: 'right' }}>
              <ChartFilterToolbar
                showWeb={this.props.showWeb}
                showPhone={this.props.showPhone}
                showF2F={this.props.showF2F}
                onToggleFilter={this.onToggleFilter}
                webLabel={'Web (' + totWeb + ')'}
                f2fLabel={'F2F (' + totF2F + ')'}
                phoneLabel={'Phone (' + totPhone + ')'}
              />
            </div>
          </div>
        </div>
        <div className={`ui seven column grid calendar-grid ${this.props.chartSampling}`}>{cards}</div>
      </Dimmer.Dimmable>
    )
  }
}

const connector: Connector<OwnProps, MappedProps> = connect(mapStateToProps, null)
export default connector(CalendarReport)



// WEBPACK FOOTER //
// ./src/reports/calendar-report.js